// Sentry monitoring for the embeddable widget. No-op unless SENTRY_DSN is
// injected at build time (esbuild `define`), so unconfigured builds stay quiet.
//
// The widget is embedded on arbitrary third-party customer sites, so we scope
// capture to errors originating from our own bundle (allowUrls) to avoid
// ingesting the host page's unrelated JS errors. PII stays off and request
// bodies / query strings are stripped, since the portal handles client data.
import * as Sentry from '@sentry/browser'

// Replaced at build time by esbuild `define`. Declared loosely so TS is happy.
declare const process: { env: Record<string, string | undefined> }

function scrubUrl(url?: string): string | undefined {
  if (typeof url === 'string' && url.includes('?')) return url.split('?')[0]
  return url
}

export function initSentry(): void {
  const env = (typeof process !== 'undefined' && process.env) || ({} as Record<string, string | undefined>)
  const dsn = env.SENTRY_DSN
  if (!dsn) return

  Sentry.init({
    dsn,
    environment: env.SENTRY_ENVIRONMENT || 'production',
    release: env.SENTRY_RELEASE || undefined,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: Number(env.SENTRY_TRACES_SAMPLE_RATE || 0.1),
    sendDefaultPii: false,
    // Only report errors whose top frame is our own bundle.
    allowUrls: [/portal\.js/, /portal\.min\.js/],
    beforeSend(event) {
      if (event.request) {
        delete (event.request as { data?: unknown }).data
        if (event.request.url) event.request.url = scrubUrl(event.request.url)
      }
      return event
    },
    beforeBreadcrumb(crumb) {
      if (crumb && crumb.data && typeof crumb.data.url === 'string') {
        crumb.data.url = scrubUrl(crumb.data.url) as string
      }
      return crumb
    },
  })
}
